class VideoScentSection extends HTMLElement {
  constructor() {
    super();
    this.hasAnimated = false;
    this.previousFocus = null;
    this.onKeydown = this.onKeydown.bind(this);
    this.onBackdropClick = this.onBackdropClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('.vss__media-trigger');
    this.modal = this.querySelector('.vss__modal');
    this.closeBtn = this.querySelector('.vss__modal-close');
    this.container = this.querySelector('.vss__video-container');

    if (!this.trigger || !this.modal || !this.container) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.closeBtn?.addEventListener('click', this.onCloseClick);
    this.modal.addEventListener('click', this.onBackdropClick);

    this.initObserver();
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    this.closeBtn?.removeEventListener('click', this.onCloseClick);
    this.modal?.removeEventListener('click', this.onBackdropClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.observer?.disconnect();
    this.closeModal({ restoreFocus: false });
  }

  initObserver() {
    if (this.reducedMotion) {
      this.classList.add('is-visible');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.classList.add('is-visible');
            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }
    );

    this.observer.observe(this);
  }

  onTriggerClick() {
    const url = this.trigger.getAttribute('data-video-url');
    if (!url) return;

    const embedHtml = this.buildEmbed(url);
    if (!embedHtml) return;

    this.container.innerHTML = embedHtml;
    this.openModal();
  }

  buildEmbed(url) {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId = '';
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      } else {
        videoId = url.split('v=')[1]?.split('&')[0] || '';
      }
      if (!videoId) return '';
      return `<iframe src="https://www.youtube.com/embed/${videoId}?autoplay=1" title="Video player" frameborder="0" allow="autoplay; encrypted-media; fullscreen" allowfullscreen></iframe>`;
    }

    if (url.includes('vimeo.com')) {
      const videoId = url.split('/').pop()?.split('?')[0] || '';
      if (!videoId) return '';
      return `<iframe src="https://player.vimeo.com/video/${videoId}?autoplay=1" title="Video player" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
    }

    if (url.endsWith('.mp4') || url.includes('.mp4?')) {
      return `<video src="${url}" controls autoplay playsinline></video>`;
    }

    return '';
  }

  openModal() {
    this.previousFocus = document.activeElement;
    this.modal.hidden = false;

    requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
    });

    document.body.classList.add('vss-modal-open');
    document.addEventListener('keydown', this.onKeydown);
    this.closeBtn?.focus();
  }

  closeModal({ restoreFocus = true } = {}) {
    if (!this.modal) return;

    this.modal.classList.remove('is-open');

    const finishClose = () => {
      this.modal.hidden = true;
      if (this.container) this.container.innerHTML = '';
      document.body.classList.remove('vss-modal-open');
      document.removeEventListener('keydown', this.onKeydown);

      if (restoreFocus && this.previousFocus && typeof this.previousFocus.focus === 'function') {
        this.previousFocus.focus();
      }
    };

    if (this.reducedMotion) {
      finishClose();
      return;
    }

    const onTransitionEnd = (event) => {
      if (event.target !== this.modal) return;
      this.modal.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    };

    this.modal.addEventListener('transitionend', onTransitionEnd);

    window.setTimeout(() => {
      if (this.modal.hidden === false && !this.modal.classList.contains('is-open')) {
        this.modal.removeEventListener('transitionend', onTransitionEnd);
        finishClose();
      }
    }, 600);
  }

  onCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeModal();
  }

  onBackdropClick(event) {
    if (event.target === this.modal) {
      this.closeModal();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal();
    }
  }
}

if (!customElements.get('video-scent-section')) {
  customElements.define('video-scent-section', VideoScentSection);
}
